import { updateCustomerDetails, getCustomerAccessToken } from '@/components/libs/storefront/customer'
import { CustomerTokenPayload } from '@/components/libs/storefront/customer.types'

type IFormActionKeys = 'password-reset' | 'password-recover' | 'update-details'

export class CustomForm extends HTMLElement {
  isValid: boolean
  formActionMap: { [key in IFormActionKeys]: (e: Event) => Promise<void> } = {
    'password-reset': this.passwordReset.bind(this),
    'password-recover': this.passwordRecover.bind(this),
    'update-details': this.updateDetails.bind(this)
  }

  constructor() {
    super()
    this.isValid = this.validFormKey()
  }

  connectedCallback() {
    const form = this.querySelector('form')

    if (!form) return
    if (!this.isValid) {
      form.innerHTML = 'Invalid form key'
      return
    }

    form.addEventListener('submit', async (e) => {
      await this.onSubmit(e)
    })
  }

  async onSubmit(e: SubmitEvent) {
    try {
      const key = this.callbackKey()
      const callback = this.formActionKeys(key)
      if (!callback) throw new Error('Form action key undefined')
      await callback(e)
    } catch (err) {
      console.log(err)
    }
  }

  /**
   * Callback key to be used in the form action
   * @returns
   */
  callbackKey(): IFormActionKeys {
    return this.dataset.action as IFormActionKeys
  }

  /** Keys to ignore when parse customer data into JSON object. */
  ignoreAttributes() {
    const ignore = this.dataset.ignore
    if (!ignore) return {}
    return JSON.parse(ignore) as {[key: string]: boolean}
  }

  /** TODO: Might be needed post launch */
  async passwordReset() {}

  /** TODO: Might be needed post launch */
  async passwordRecover() {}

  /**
   * Sadly shopify does not offer an native form for updating customer details.
   *
   * Using the storefront Api to update customer details
   * @param {*} e
   */
  async updateDetails(e: Event) {
    e.preventDefault()
    const emailRef = this.dataset.emailRef
    const { json, formData } = this.parseFormData(e, this.ignoreAttributes())
    const password = formData.get('password')

    if (!password || !json.email || !emailRef) {
      console.error('Password and Email are required')
      return
    }
    const customerAccessToken = await getCustomerAccessToken(json.email, password.toString())
    await updateCustomerDetails(customerAccessToken, json, emailRef)
  }

  /**
   *
   * Parse the FormData into a JSON object
   * returns both form data and json object.
   *
   * Optional ignore keys which wont be parsed into the object.
   *
   * @param {*} e
   * @param {*} ignoreKeys
   */
  parseFormData(e: Event, ignoreKeys: {[key: string]: boolean} = {}) {
    const formData = new FormData(e.target as EventTarget as HTMLFormElement)
    const data: CustomerTokenPayload = {}
    formData.forEach((value, key) => {
      if (!value || ignoreKeys[key]) return
      const dataKey = this.snakeToCamel(key) as keyof CustomerTokenPayload
      data[dataKey] = value.toString()
    })
    return {
      json: data,
      formData
    }
  }

  /**
   *
   * @param {*} str
   * @returns
   */
  snakeToCamel(str: string) {
    return str.toLowerCase().replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''))
  }

  /**
   *
   * @param {IFormActionKeys} key
   * @returns
   */
  formActionKeys(key: IFormActionKeys) {
    if (!key || !(key in this.formActionMap)) return
    return this.formActionMap[key]
  }

  /**
   * Checks if the callback key is valid
   * @returns boolean
   */
  validFormKey() {
    return !!this.formActionKeys(this.callbackKey())
  }
}
